import React from "react";
import { useSessionContext } from "./SessionContext";
import { listTasks } from "core/services/site/task";
import { TaskType } from "core/models/Task";
import { useNotifications } from "ui/context/NotificationsContext";

const DEFAULT_TIMEOUT_DURATION = 10000; // time beween polls (in ms)
const MAX_ITERATION_COUNT = 20; // Max number of polls
interface Edit {
    fragmentId: number | undefined;
    taskId: number;
    editType: TaskType;
    notificationId?: string;
    successText: string;
}
interface FinishedTask {
    fragmentId: number | undefined;
    successText: string;
    taskId: number;
    editType: TaskType;
    notified: boolean;
    status: "success" | "error";
    notificationId?: string;
}

interface QuedTask {
    taskId: number;
    notificationId: string;
    successText: string;
}
type TaskContextType = {
    runningTasks: Edit[];
    finishedTasks: FinishedTask[];
    update(): void;
    newTask(taskId: number, notificationId: string, successText: string): void;
};

const TaskContext = React.createContext<TaskContextType | null>(null);

const useTaskContext = (): TaskContextType => {
    const context = React.useContext(TaskContext);
    if (!context) {
        throw Error("Unable to create task context");
    }
    return context;
};

type Props = {
    children: React.ReactNode;
};

const TaskContextProvider: React.FC<Props> = ({ children }) => {
    const sessionContext = useSessionContext();
    const { addNotification, updateNotification } = useNotifications();

    const finishedTasks = React.useRef<FinishedTask[]>([]);
    const runningTasks = React.useRef<Edit[]>([]);
    const [, setPending] = React.useState<boolean>(false);
    const timeoutDuration = React.useRef(DEFAULT_TIMEOUT_DURATION); // ms
    const taskQueue = React.useRef<QuedTask[]>([]);
    const iterationCount = React.useRef(0);
    const timer = React.useRef<NodeJS.Timeout | null>(null);

    const getTasks = React.useCallback(async (): Promise<void> => {
        setPending(true);
        try {
            if (!sessionContext.session || !sessionContext.selectedProject) {
                throw new Error("No session or project");
            }
            if (!sessionContext.verified) {
                throw new Error("User not verified");
            }
            const latestTasks = await listTasks(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                7,
            );
            if (latestTasks.error || !latestTasks.value) {
                throw new Error("getting latest tasks failed");
            }

            const processingTasks = latestTasks.value.filter(
                (task) => task.processing,
            );
            iterationCount.current =
                processingTasks.length > 0 ? 0 : iterationCount.current + 1;
            timeoutDuration.current =
                processingTasks.length > 0 ? 3000 : DEFAULT_TIMEOUT_DURATION;

            const newRunningTasks: Edit[] = processingTasks
                .filter((task) => task.getType() !== "other")
                .map((task) => {
                    const editType = task.getType();
                    return {
                        fragmentId: task.message.data.fragmentId,
                        taskId: task.id,
                        successText:
                            taskQueue.current.find(
                                (quedTask) => quedTask.taskId === task.id,
                            )?.successText || `${editType} was finished`,
                        editType,
                        notificationId: taskQueue.current.find(
                            (queuedTask) => queuedTask.taskId === task.id,
                        )?.notificationId,
                    };
                });

            const newFinished = runningTasks.current.filter(
                (oldTask) =>
                    !newRunningTasks.some(
                        (newTask) => newTask.taskId === oldTask.taskId,
                    ),
            );
            if (newFinished.length > 0) {
                const newFinishedChecked: FinishedTask[] = newFinished.map(
                    (task) => {
                        const taskStatusCheck = latestTasks.value?.find(
                            (t) => t.id === task.taskId,
                        );
                        let taskStatus: "success" | "error" = "success";
                        if (!taskStatusCheck) {
                            taskStatus = "error";
                        }
                        if (
                            taskStatusCheck &&
                            !taskStatusCheck.results.pop()?.result
                        ) {
                            // If last task result is false, error
                            taskStatus = "error";
                        }
                        return {
                            ...task,
                            notified: false,
                            status: taskStatus,
                        };
                    },
                );
                const newFinishedTasks: FinishedTask[] =
                    finishedTasks.current.concat(newFinishedChecked);
                finishedTasks.current = newFinishedTasks;
            }
            runningTasks.current = newRunningTasks;
            setPending(false);
        } catch (err) {
            setPending(false);
        }
    }, [sessionContext]);

    const update = React.useCallback((): void => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        if (
            !sessionContext.session?.token ||
            !sessionContext.selectedProject?.id ||
            !sessionContext.verified
        ) {
            return;
        }
        getTasks();
        if (iterationCount.current > MAX_ITERATION_COUNT) {
            return;
        }
        timer.current = setTimeout(update, timeoutDuration.current);
    }, [sessionContext, getTasks]);

    const newTask = React.useCallback(
        async (
            taskId: number,
            notificationId: string,
            successText: string,
        ): Promise<void> => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            let newTaskQueue: QuedTask[] = [...taskQueue.current];
            if (newTaskQueue.some((t) => t.taskId === taskId)) {
                // Remove task if already in que
                newTaskQueue = newTaskQueue.filter((t) => t.taskId !== taskId);
            }
            newTaskQueue.push({ taskId, notificationId, successText });
            taskQueue.current = newTaskQueue;
            iterationCount.current = 0;
            update();
        },
        [update],
    );

    React.useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        if (
            sessionContext.session?.token ||
            sessionContext.selectedProject?.id ||
            sessionContext.verified
        ) {
            timer.current = setTimeout(update, timeoutDuration.current);
        }
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        };
    }, [sessionContext, update]);

    React.useEffect(() => {
        const notificationRequired = finishedTasks.current.filter(
            (t) => !t.notified,
        );
        if (notificationRequired.length === 0) {
            return;
        }
        const newFinishedTaks = finishedTasks.current
            .filter((t) => !t.notified)
            .map((task) => {
                if (task.notificationId) {
                    updateNotification(
                        task.notificationId,
                        "success",
                        `${task.editType} finished`,
                        task.successText,
                        5000,
                    );
                } else {
                    addNotification(
                        "success",
                        `${task.editType} finished`,
                        task.successText,
                        5000,
                    );
                }
                return {
                    ...task,
                    notified: true,
                };
            });
        finishedTasks.current = newFinishedTaks;
    });

    const context: TaskContextType = {
        runningTasks: runningTasks.current,
        finishedTasks: finishedTasks.current,
        update,
        newTask,
    };

    return (
        <TaskContext.Provider value={context}>{children}</TaskContext.Provider>
    );
};

export default TaskContext;
export { TaskContextProvider, useTaskContext };
